import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo } from "react";
import { aggridHeaderClass, SmallTitleWithSubSpan } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { HRM3040_R01PreviewDto, defaultHRM3040_R01PreviewDto } from "./Dto_HRM3040_R01";
import { useHRM3040_R01Store } from "./Store_HRM3040_R01";
import { previewHeaderNames_HRM3040_R01 as headerNames } from "./Util_HRM3040_R01";

/** 조회 전용 미리보기 배치 저장 키이다. */
const COLUMN_STATE_KEY = 'HRM3040_R01-preview-grid-column-state';
/** 미리보기 그리드를 가리키는 참조를 전달받는다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM3040_R01PreviewDto> | null>; // 조회 후 스크롤 이동용 그리드
}

/** 서버가 계산한 재직자별 수당과 건수·세 금액 합계를 조회 전용 표로 반환한다. */
export const PreviewTable_HRM3040_R01 = ({ gridRef }: Props) => {
    // 목록과 마지막 조회 조건, 이 표의 배치·스크롤만 화면 Store에서 읽는다.
    const {
        HRM3040_R01PreviewList, // 서버가 계산한 사원별 수당 미리보기 목록
        HRM3040_R01PastFilter, // 마지막 성공 조회에 사용한 검색 조건
        previewColumnState, // 미리보기 표의 컬럼 배치
        setPreviewColumnState, // 미리보기 표의 컬럼 배치를 저장하는 함수
        previewScrollTop, // 미리보기 표의 스크롤 위치
        setPreviewScrollTop, // 미리보기 표의 스크롤 위치를 저장하는 함수
    } = useHRM3040_R01Store();
    // 직책 표시명은 공통 코드 Store에서 가져오며 목록이 없으면 원본 코드를 유지한다.
    const { jcCdList } = useCommonCodeStore();
    // HRM1040 조회 표와 같은 ID·표시 목록·CSS 설정이다.
    const { getRowId, createRowClassRules, createCellClassRules, paddedGridData } = useGridSettingContent<HRM3040_R01PreviewDto>({
        gridRef, key: 'valueIndex', gridData: HRM3040_R01PreviewList, defaultGridData: defaultHRM3040_R01PreviewDto, selectedRow: undefined,
    });
    // 직책 코드 목록이 준비되거나 바뀔 때만 컬럼 표시 정의를 갱신한다.
    const baseColumnDefs = useMemo<ColDef<HRM3040_R01PreviewDto>[]>(() => [
        // 공통 기본 index 렌더러는 번호만 표시하며 하단은 빈칸이다.
        ...getDefaultRowIndexColumnConfig(),
        { headerName: headerNames.empNo, colId: 'empNo', field: 'empNo', width: 100 },
        { headerName: headerNames.empNm, colId: 'empNm', field: 'empNm', width: 84 },
        { headerName: headerNames.orgNm, colId: 'orgNm', field: 'orgNm', width: 96 },
        { headerName: headerNames.empDt, colId: 'empDt', field: 'empDt', width: 100 },
        { headerName: headerNames.serviceYears, colId: 'serviceYears', field: 'serviceYears', width: 72 },
        {
            headerName: headerNames.serviceAllowAmt, colId: 'serviceAllowAmt', field: 'serviceAllowAmt', width: 100,
            // 삼항 연산자로 미지정 값은 빈칸, 금액은 정수 천 단위 형식으로 표시한다. 저장값은 바꾸지 않는다.
            valueFormatter: params => params.value == null ? '' : Number(params.value).toLocaleString('ko-KR', { maximumFractionDigits: 0 }),
        },
        {
            headerName: headerNames.jobPosition, colId: 'jobPosition', field: 'jobPosition', width: 80,
            // ?.·??: 코드명 조회 실패 또는 준비 전에는 받은 원본 코드를 표시한다.
            valueFormatter: params => jcCdList.find(code => code.minorCd === params.value)?.codeNm ?? params.value ?? '',
        },
        {
            headerName: headerNames.positionAllowAmt, colId: 'positionAllowAmt', field: 'positionAllowAmt', width: 100,
            valueFormatter: params => params.value == null ? '' : Number(params.value).toLocaleString('ko-KR', { maximumFractionDigits: 0 }),
        },
        {
            headerName: headerNames.totalAllowAmt, colId: 'totalAllowAmt', field: 'totalAllowAmt', width: 110,
            valueFormatter: params => params.value == null ? '' : Number(params.value).toLocaleString('ko-KR', { maximumFractionDigits: 0 }),
        },
    ], [jcCdList]);
    // 현재 컬럼에 맞는 기본 배치를 기억한다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    // 저장 배치가 null이면 기본 정의, 그 외에는 공통 복원 결과를 그대로 반환한다.
    const columnDefs = useMemo(() => {
        if (previewColumnState === null) return baseColumnDefs;
        return GridCustomColumnDefs<HRM3040_R01PreviewDto>({ baseColumnDefs, columnState: previewColumnState, headerNames, fallbackHeaderClass: aggridHeaderClass });
    }, [baseColumnDefs, previewColumnState]);
    // 미리보기의 실제 행만 집계한다. 프런트는 수당을 계산하지 않고 서버 결과만 합산한다.
    const pinnedBottomRow = useMemo(() => [{
        empNo: `${HRM3040_R01PreviewList.length.toLocaleString()}건`,
        serviceAllowAmt: HRM3040_R01PreviewList.reduce((sum, row) => sum + row.serviceAllowAmt, 0),
        positionAllowAmt: HRM3040_R01PreviewList.reduce((sum, row) => sum + row.positionAllowAmt, 0),
        totalAllowAmt: HRM3040_R01PreviewList.reduce((sum, row) => sum + row.totalAllowAmt, 0),
    }], [HRM3040_R01PreviewList]);
    // 선택·편집은 없고 하단 건수·금액은 공통 규칙으로 가운데 정렬한다.
    const cellCssDefaultColDef = {
        ...defaultColDef, editable: false,
        cellClassRules: createCellClassRules<HRM3040_R01PreviewDto>({
            idKey: 'valueIndex', selectedList: [], deletedList: [], disabledInsertedColumns: [],
            disabledColumns: [], pointerInsertedColumns: [], pointerColumns: [],
        }),
    };
    return (
        // 조회 전용 표의 우클릭과 배치 저장은 공통 Wrapper를 사용한다.
        <GridContextWrapper<HRM3040_R01PreviewDto & { inserted?: boolean }>
            gridRef={gridRef}
            paddedGridData={paddedGridData}
            defaultRowData={defaultHRM3040_R01PreviewDto}
            baseColumnDefs={columnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setPreviewColumnState}
            localStorageKey={COLUMN_STATE_KEY}>

            {/* 마지막 조회 기준일이 있어야 사업장·부서 조건 적용 안내를 표시한다. */}
            <SmallTitleWithSubSpan title="적용 미리보기 (재직자)"
                subTitle={HRM3040_R01PastFilter.baseDt ? `${HRM3040_R01PastFilter.baseDt} 기준 · 사업장·부서 조건 적용` : undefined} />

            {/* 행 선택·편집 핸들러 없이 서버 결과와 하단 집계만 표시한다. */}
            <Grid_Excel<HRM3040_R01PreviewDto>
                ref={gridRef}
                rowData={paddedGridData}
                getRowId={getRowId}
                columnDefs={columnDefs}
                defaultColDef={cellCssDefaultColDef}

                // 공통 행 CSS의 타입 조건만 충족한다. inserted는 true일 때 신규라는 뜻이며 조회 DTO에는 추가하지 않는다.
                rowClassRules={createRowClassRules<HRM3040_R01PreviewDto & { inserted?: boolean }>({ idKey: 'valueIndex', selectedList: [], deletedList: [] })}
                pinnedBottomRowData={pinnedBottomRow}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setPreviewColumnState}
                getDefaultColumnState={() => defaultColumnState}
                scrollTop={previewScrollTop}
                setScrollTop={setPreviewScrollTop} />
        </GridContextWrapper>
    );
};
